import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Switch, FormControlLabel, TextField,
  Button, Grid, Alert, Divider, Chip, MenuItem, CircularProgress, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer, Tabs, Tab,
} from '@mui/material';
import {
  Hub, Save, CalendarMonth, SupportAgent, Schedule, EventAvailable,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../config/api';

const DIAS = [
  { key: 'lun', label: 'Lunes' }, { key: 'mar', label: 'Martes' }, { key: 'mie', label: 'Miércoles' },
  { key: 'jue', label: 'Jueves' }, { key: 'vie', label: 'Viernes' }, { key: 'sab', label: 'Sábado' },
  { key: 'dom', label: 'Domingo' },
];

const Orquestacion: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [cfg, setCfg] = useState<any>(null);
  const [citas, setCitas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [horario, setHorario] = useState<Record<string, [string, string]>>({});

  const cargar = () => {
    setLoading(true);
    Promise.all([
      api.get('/orquestacion/config'),
      api.get('/orquestacion/citas'),
    ]).then(([c, ci]) => {
      setCfg(c.data.config);
      setHorario(c.data.config.horario_atencion || {});
      setCitas(ci.data.citas || []);
    }).finally(() => setLoading(false));
  };
  useEffect(cargar, []);

  const set = (k: string) => (e: any) => setCfg({ ...cfg, [k]: e.target.checked !== undefined && typeof e.target.checked === 'boolean' && e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const toggleDia = (dia: string) => {
    const h = { ...horario };
    if (h[dia]) delete h[dia];
    else h[dia] = ['09:00', '18:00'];
    setHorario(h);
  };
  const setHora = (dia: string, idx: 0 | 1, val: string) => {
    const h = { ...horario };
    const par: [string, string] = h[dia] ? [...h[dia]] as [string, string] : ['09:00', '18:00'];
    par[idx] = val;
    h[dia] = par;
    setHorario(h);
  };

  const guardar = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await api.put('/orquestacion/config', { ...cfg, horario_atencion: horario });
      setCfg(data.config);
      setMsg('Configuración guardada');
    } catch { setMsg('Error al guardar'); } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (!cfg) return <Alert severity="error">No se pudo cargar la configuración.</Alert>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Hub color="primary" />
        <Typography variant="h4">Orquestación del Agente</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Define qué puede <strong>hacer</strong> tu agente (no solo responder): agendar citas, derivar a un humano y respetar tu horario.
      </Typography>

      {msg && <Alert severity={msg.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<Hub />} iconPosition="start" label="Herramientas" />
        <Tab icon={<Schedule />} iconPosition="start" label="Horario" />
        <Tab icon={<EventAvailable />} iconPosition="start" label={`Citas (${citas.length})`} />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <CalendarMonth sx={{ color: '#4285F4' }} />
                  <Typography variant="h6">Agendar citas</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  El agente detecta cuando un cliente quiere reservar, revisa tu Agenda y solo ofrece horas libres dentro de tu horario.
                  Si conectas Google Calendar, también evita tus eventos de Google y copia cada cita a tu calendario.
                </Typography>
                <FormControlLabel control={<Switch checked={!!cfg.tool_agendar} onChange={(e) => setCfg({ ...cfg, tool_agendar: e.target.checked })} />} label="Permitir agendar citas" />
                <Divider sx={{ my: 2 }} />
                <TextField type="number" label="Duración de cita (min)" fullWidth sx={{ mb: 2 }} value={cfg.duracion_cita_min} onChange={(e) => setCfg({ ...cfg, duracion_cita_min: parseInt(e.target.value) || 30 })} inputProps={{ min: 5, max: 600, step: 5 }} />
                <TextField type="number" label="Anticipación mínima (min)" fullWidth sx={{ mb: 2 }} value={cfg.anticipacion_min ?? 60} onChange={(e) => setCfg({ ...cfg, anticipacion_min: Math.max(0, parseInt(e.target.value) || 0) })} inputProps={{ min: 0, step: 15 }} helperText="Ej: 60 = no se puede agendar con menos de 1 hora de aviso." />
                <TextField label="Zona horaria" fullWidth value={cfg.zona_horaria} onChange={(e) => setCfg({ ...cfg, zona_horaria: e.target.value })} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <SupportAgent sx={{ color: '#F59E0B' }} />
                  <Typography variant="h6">Derivar a humano (handoff)</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Si el cliente pide hablar con una persona, el bot deja de responder y te avisa para que tomes la conversación.
                </Typography>
                <FormControlLabel control={<Switch checked={!!cfg.tool_handoff} onChange={(e) => setCfg({ ...cfg, tool_handoff: e.target.checked })} />} label="Permitir derivar a humano" />
                <TextField label="Número del asesor (opcional)" fullWidth sx={{ mt: 2, mb: 2 }} value={cfg.numero_handoff || ''} onChange={(e) => setCfg({ ...cfg, numero_handoff: e.target.value })} placeholder="+56 9 1234 5678" />
                <TextField label="Mensaje al derivar" fullWidth multiline rows={2} value={cfg.mensaje_handoff || ''} onChange={(e) => setCfg({ ...cfg, mensaje_handoff: e.target.value })} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Horario de atención</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Marca los días y horas en que el agente responde y ofrece citas. Fuera de ese horario envía un mensaje automático.
              Si no marcas ningún día, el agente responde siempre y ofrece citas de lunes a viernes de 9:00 a 18:00.
            </Typography>
            <Stack spacing={1.5}>
              {DIAS.map((d) => (
                <Stack key={d.key} direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
                  <FormControlLabel sx={{ width: { xs: 110, sm: 150 }, mr: 0 }} control={<Switch checked={!!horario[d.key]} onChange={() => toggleDia(d.key)} />} label={d.label} />
                  {horario[d.key] && (
                    <>
                      <TextField type="time" size="small" sx={{ width: 120 }} value={horario[d.key][0]} onChange={(e) => setHora(d.key, 0, e.target.value)} />
                      <Typography>a</Typography>
                      <TextField type="time" size="small" sx={{ width: 120 }} value={horario[d.key][1]} onChange={(e) => setHora(d.key, 1, e.target.value)} />
                    </>
                  )}
                </Stack>
              ))}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <TextField label="Mensaje fuera de horario" fullWidth multiline rows={2} value={cfg.mensaje_fuera_horario || ''} onChange={(e) => setCfg({ ...cfg, mensaje_fuera_horario: e.target.value })} />
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Últimas citas. Para verlas en calendario, moverlas o crear nuevas, usa la Agenda.</Typography>
              <Button variant="outlined" size="small" startIcon={<CalendarMonth />} onClick={() => navigate('/app/agenda')}>Abrir agenda</Button>
            </Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {citas.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.nombre_cliente}</TableCell>
                      <TableCell>{c.telefono_cliente}</TableCell>
                      <TableCell>{new Date(c.inicio).toLocaleString('es-CL')}</TableCell>
                      <TableCell><Chip size="small" label={c.estado} color={c.estado === 'confirmada' ? 'success' : 'default'} /></TableCell>
                    </TableRow>
                  ))}
                  {citas.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>Aún no hay citas agendadas por el agente</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab !== 2 && (
        <Box sx={{ position: 'sticky', bottom: 16, mt: 3, textAlign: 'right' }}>
          <Button variant="contained" size="large" startIcon={<Save />} onClick={guardar} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Orquestacion;
